import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { MoreHorizontal } from "lucide-react";
import Table from "../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import GlobalPagination from "../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { useRowActionMenu } from "../../../hooks/useRowActionMenu";
import { PIPELINE_SORTABLE_COLUMNS, PIPELINE_SORTABLE_HEADERS } from "./mappers";
import { Chip, OnboardProgress, RiskChip } from "./ui";

const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Carrier",
  "Office Code",
  "MC / DOT",
  "Equipment",
  "Stage",
  "Onboarding Status",
  "Risk",
  "Last Activity",
  "Score",
];

const DATA_COLUMN_MIN_WIDTH = 200;
const DATA_COLUMN_MAX_WIDTH = 420;
const SELECTION_COLUMN_WIDTH = 80;
const ACTION_COLUMN_WIDTH = 50;

const TD_CLASS =
  "px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none";

const CHECKBOX_CLASS =
  "relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold";

function formatCarrierRow(c) {
  return {
    id: c.id,
    Carrier: c.name,
    "Office Code": c.officeCode || "—",
    "MC / DOT": `${c.mc} / ${c.dot}`,
    Equipment: (c.equipment || []).join(", "),
    Stage: <Chip>{c.stage}</Chip>,
    "Onboarding Status": (
      <OnboardProgress done={c.onboardStepsDone} total={c.onboardStepsTotal} />
    ),
    Risk: <RiskChip risk={c.risk} />,
    "Last Activity": c.lastActivity || "—",
    Score: c.score ?? "—",
  };
}

export default function PipelineGrid({
  carriers = [],
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  itemsPerPage = 50,
  loading = false,
  errorMessage = "",
  onPageChange,
  onItemsPerPageChange,
  sortBy = null,
  ascending = false,
  onSort,
  onOpenCarrier,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedCellKey, setExpandedCellKey] = useState(null);
  const checkboxHeaderRef = useRef(null);
  const {
    openRowId,
    position: dropdownPos,
    dropdownRef,
    handleActionClick,
    closeMenu,
  } = useRowActionMenu();

  const pageRows = carriers.map(formatCarrierRow);
  const dataHeaders = TABLE_HEADERS.slice(2);

  const handleSelectAll = () => {
    const pageIds = pageRows.map((r) => r.id);
    const allSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full min-h-0 transition-none">
      {errorMessage ? (
        <div className="mx-fluid-1 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-fluid-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="flex-1 min-h-0 flex flex-col bg-white dark:bg-acs-dark-surface overflow-hidden">
        <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none relative">
          <Table
            headers={TABLE_HEADERS}
            data={pageRows}
            selectedRows={selectedIds}
            totalValidRows={pageRows.length}
            onSelectAll={handleSelectAll}
            renderCheckboxActions={() => {}}
            checkboxHeaderRef={checkboxHeaderRef}
            autoGrowColumns
            dataColumnMinWidth={DATA_COLUMN_MIN_WIDTH}
            dataColumnMaxWidth={DATA_COLUMN_MAX_WIDTH}
            enableColumnResize
            nonHideableColumnIds={["Carrier"]}
            sortBy={sortBy}
            ascending={ascending}
            sortableHeaders={PIPELINE_SORTABLE_HEADERS}
            sortKeyByHeader={(h) => PIPELINE_SORTABLE_COLUMNS[h]}
            onSort={(header) => {
              const key = PIPELINE_SORTABLE_COLUMNS[header];
              if (key) onSort?.(key);
            }}
          >
            {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
              const orderedDataHeaders = (visibleColumns ?? [])
                .map((col) => col.id)
                .filter((id) => id !== "Selection" && id !== "Action");
              const headersToRender = orderedDataHeaders.length
                ? orderedDataHeaders
                : dataHeaders;
              const { className: checkboxClass, style: checkboxStyle } =
                getStickyCellProps("Selection");
              const { className: actionClass, style: actionStyle } =
                getStickyCellProps("Action");
              const isMenuOpen = openRowId === row.id;

              return (
                <>
                  <td
                    className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${checkboxClass}`}
                    style={{
                      ...checkboxStyle,
                      width: `${SELECTION_COLUMN_WIDTH}px`,
                      minWidth: `${SELECTION_COLUMN_WIDTH}px`,
                      maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                    }}
                  >
                    <input
                      type="checkbox"
                      className={CHECKBOX_CLASS}
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </td>

                  <td
                    onClick={(e) => e.stopPropagation()}
                    className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${actionClass}`}
                    style={{
                      ...actionStyle,
                      width: `${ACTION_COLUMN_WIDTH}px`,
                      minWidth: `${ACTION_COLUMN_WIDTH}px`,
                      maxWidth: `${ACTION_COLUMN_WIDTH}px`,
                    }}
                  >
                    <button
                      type="button"
                      data-action-trigger
                      className="p-1 relative w-full transition-none text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
                      onClick={(e) => handleActionClick(row.id, e)}
                    >
                      <MoreHorizontal size={18} className="mx-auto" />
                    </button>
                  </td>

                  {isMenuOpen &&
                    ReactDOM.createPortal(
                      <div
                        ref={dropdownRef}
                        style={{
                          position: "absolute",
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                          zIndex: 9999,
                        }}
                      >
                        <ActionMenu
                          rowId={row.id}
                          isOpen
                          onClose={closeMenu}
                          moduleName="Carrier Sourcing"
                          actions={[
                            {
                              icon: "Eye",
                              label: "View",
                              color: "blue",
                              action: () => {
                                closeMenu();
                                onOpenCarrier(row.id);
                              },
                            },
                          ]}
                        />
                      </div>,
                      document.body
                    )}

                  {headersToRender.map((header) => {
                    const cellKey = `${row.id}-${header}`;
                    const isExpanded = expandedCellKey === cellKey;
                    const { className: colClass, style: colStyle } =
                      getStickyCellProps(header);

                    return (
                      <td
                        key={header}
                        className={`${TD_CLASS} ${colClass}`}
                        onDoubleClick={() => onOpenCarrier(row.id)}
                        onMouseEnter={() => setExpandedCellKey(cellKey)}
                        onMouseLeave={() => setExpandedCellKey(null)}
                        style={{
                          ...colStyle,
                          whiteSpace: isExpanded ? "normal" : "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          minWidth: `${
                            columnWidths?.[header] || DATA_COLUMN_MIN_WIDTH
                          }px`,
                          maxWidth: isExpanded
                            ? `${DATA_COLUMN_MAX_WIDTH * 2}px`
                            : `${DATA_COLUMN_MAX_WIDTH}px`,
                          width: columnWidths?.[header]
                            ? `${columnWidths[header]}px`
                            : `${DATA_COLUMN_MIN_WIDTH}px`,
                          transition: "max-width 120ms ease",
                        }}
                      >
                        {row[header]}
                      </td>
                    );
                  })}
                </>
              );
            }}
          </Table>

          {loading ? (
            <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60 dark:bg-black/30">
              <Loader
                color="text-blue-600 dark:text-acs-dark-text"
                size="h-5 w-5"
                message="Loading carriers..."
              />
            </div>
          ) : null}

          {!loading && pageRows.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                {totalCount === 0
                  ? "No carriers yet. Use Add Carrier to add a prospect."
                  : "No carriers match your filters."}
              </p>
            </div>
          ) : null}
        </div>

        <GlobalPagination
          label="Carriers per page"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      </div>
    </div>
  );
}
